import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent } from '../types/events';
import type { IntegrationFailureData } from '../pages/events/event-renderers';

/**
 * A live-received integration event, normalized for the deliveries UI.
 * This only carries the fields needed for the row (status, delivery id,
 * error info). Request/response details for the expanded view are fetched
 * separately from the backend per delivery — see IntegrationDeliveriesCard.
 */
export interface LiveIntegrationEvent {
  eventId: string; // platform evt-...
  /**
   * The integration event-log row id (iev-...) this platform event refers to.
   * Read from the platform event envelope's `subjectId`, which the backend
   * sets to the matching event-log row id on both success and failure events.
   * Used as the dedupe key against REST-fetched historical rows.
   */
  integrationEventId?: string;
  eventType: string; // com.github.push | integration.event.failed | ...
  createdAt: string; // ISO timestamp
  status: 'success' | 'failed';
  errorMessage?: string;
  provider: string;
  failure?: {
    stage: 'parse' | 'adapter-missing' | 'pipeline';
    providerEventType: string;
  };
}

const INTEGRATION_SOURCE = 'optr.conn.integrations';
const MAX_LIVE_EVENTS = 200;

function isFailure(eventType: string): boolean {
  return eventType === 'integration.event.failed';
}

function mapPlatformEvent(event: PlatformEvent): LiveIntegrationEvent | null {
  const data = event.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') return null;
  const integrationId = data.integrationId as string | undefined;
  if (!integrationId) return null;

  // Backend sets `subjectId` to the iev- event-log row id on integration
  // platform events. Only trust it when `subjectType` confirms that's what
  // it refers to, so we don't accidentally correlate against other envelopes.
  const envelopeIntegrationEventId =
    event.subjectType === 'integration-event' ? event.subjectId : undefined;

  if (isFailure(event.eventType)) {
    // NOTE: `data.deliveryId` here is the *provider's* delivery id (e.g.
    // X-GitHub-Delivery) — not the iev- event-log row id. It must not be used
    // as the dedupe key against historical rows.
    const d = data as unknown as IntegrationFailureData;
    return {
      eventId: event.eventId,
      integrationEventId: envelopeIntegrationEventId,
      eventType: event.eventType,
      createdAt: event.metadata.timestamp,
      status: 'failed',
      errorMessage: d.errorMessage,
      provider: d.provider,
      failure: {
        stage: d.stage,
        providerEventType: d.providerEventType,
      },
    };
  }

  const d = data as {
    integrationId: string;
    provider: string;
    integrationEvent?: { id?: string };
  };
  return {
    eventId: event.eventId,
    integrationEventId: envelopeIntegrationEventId ?? d.integrationEvent?.id,
    eventType: event.eventType,
    createdAt: event.metadata.timestamp,
    status: 'success',
    provider: d.provider,
  };
}

/**
 * Subscribes to the platform WebSocket for events matching a specific
 * integration. Keeps a local buffer of live events so the UI can show the
 * full normalized payload (only available on the wire, not the REST list
 * endpoint), and also invalidates react-query caches so the Recent Events
 * and Statistics cards refresh.
 */
export function useIntegrationLiveEvents(
  integrationId: string | undefined,
  /**
   * react-query key prefix of the historical events list to invalidate when a
   * live event arrives. Defaults to the legacy `['integrations', id]` prefix.
   * Callers pass their surface's real prefix (e.g.
   * `['space', spaceId, 'integrations', id]` or `['org', 'integrations', id]`)
   * so the historical list and stats actually refetch.
   */
  queryKeyPrefix?: readonly unknown[],
) {
  const { subscribe } = usePlatformEvents();
  const qc = useQueryClient();
  const [liveEvents, setLiveEvents] = useState<LiveIntegrationEvent[]>([]);

  // Hold the latest prefix in a ref so callers can pass an inline array
  // literal (new identity each render) without churning the subscribe effect.
  const prefixRef = useRef(queryKeyPrefix);
  prefixRef.current = queryKeyPrefix;

  useEffect(() => {
    if (!integrationId) {
      setLiveEvents([]);
      return;
    }

    // Reset buffer when the integration id changes.
    setLiveEvents([]);

    const unsub = subscribe([{}], (event: PlatformEvent) => {
      if (event.source !== INTEGRATION_SOURCE) return;
      const data = event.data as { integrationId?: string } | undefined;
      if (data?.integrationId !== integrationId) return;

      const mapped = mapPlatformEvent(event);
      if (!mapped) return;

      setLiveEvents((prev) => {
        if (prev.some((e) => e.eventId === mapped.eventId)) return prev;
        return [mapped, ...prev].slice(0, MAX_LIVE_EVENTS);
      });

      // Keep historical list + integration stats fresh.
      const prefix = prefixRef.current ?? ['integrations', integrationId];
      qc.invalidateQueries({ queryKey: [...prefix, 'events'] });
      qc.invalidateQueries({ queryKey: [...prefix] });
    });

    return unsub;
  }, [subscribe, qc, integrationId]);

  return liveEvents;
}
